/**
 * User Management Component
 * Coláiste Lurgan Management System - Manage user accounts and their permissions
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { useImpersonation } from '../../hooks/useImpersonation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useToast } from '../../hooks/use-toast';
import {
  useUsers,
  useUserStats,
  useUpdateUser,
  useCreateUser,
  useResendVerification,
  useExpiredParentVerificationsCount,
  useResendExpiredParentVerifications,
} from '../../hooks/usePermissions';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { 
  Users, 
  Plus, 
  Search, 
  MoreHorizontal,
  Edit, 
  Shield, 
  Eye,
  UserCheck,
  UserX,
  Mail,
  Crown,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  LogIn,
  Send
} from 'lucide-react';


const getUserTypeInfo = (t) => ({
  'ADMIN': { 
    name: t('userRole.ADMIN'),
    color: 'bg-red-100 text-red-800 border-red-200'
  },
  'TEACHER': { 
    name: t('userRole.TEACHER'),
    color: 'bg-blue-100 text-blue-800 border-blue-200'
  },
  'OFFICE': { 
    name: t('userRole.OFFICE'),
    color: 'bg-green-100 text-green-800 border-green-200'
  },
  'HOUSE_OWNER': { 
    name: t('userRole.HOUSE_OWNER'),
    color: 'bg-orange-100 text-orange-800 border-orange-200'
  },
  'INTERVIEWER': { 
    name: t('userRole.INTERVIEWER'),
    color: 'bg-purple-100 text-purple-800 border-purple-200'
  },
  'PARENT': { 
    name: t('userRole.PARENT'),
    color: 'bg-pink-100 text-pink-800 border-pink-200'
  }
});

const PAGE_SIZE = 25;

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({
    id: null,
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'TEACHER'
  });
  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'TEACHER'
  });
  const { toast } = useToast();
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { startImpersonating } = useImpersonation();
  const [isResendExpiredDialogOpen, setIsResendExpiredDialogOpen] = useState(false);
  const [expiredParentCount, setExpiredParentCount] = useState(0);

  const handleImpersonate = async (user) => {
    try {
      await startImpersonating(user.id);
    } catch (error) {
      toast({
        title: t('impersonation.errorTitle'),
        description: error?.response?.data?.error || t('common.error'),
        variant: 'destructive',
      });
    }
  };

  const checkExpiredParentsMutation = useExpiredParentVerificationsCount({
    onSuccess: (data) => {
      const count = data?.count || 0;
      if (count === 0) {
        toast({ description: t('users.resendExpiredParentsNone') });
        return;
      }
      setExpiredParentCount(count);
      setIsResendExpiredDialogOpen(true);
    },
    onError: () => {
      toast({
        description: t('users.resendExpiredParentsCheckFailed'),
        variant: 'destructive',
      });
    },
  });

  const resendExpiredParentsMutation = useResendExpiredParentVerifications({
    onSuccess: (data) => {
      setIsResendExpiredDialogOpen(false);
      toast({
        description: t('users.resendExpiredParentsSuccess', { count: data?.count || 0 }),
      });
    },
    onError: () => {
      toast({
        description: t('users.resendExpiredParentsFailed'),
        variant: 'destructive',
      });
    },
  });

  // Debounce search input to avoid firing an API call on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [typeFilter, statusFilter]);

  // Get translated user type info
  const userTypeInfo = getUserTypeInfo(t);

  // Fetch users from API (server handles all filtering + pagination)
  const {
    data: usersResponse,
    isLoading,
    error,
    refetch
  } = useUsers({
    search: debouncedSearch,
    user_type: typeFilter,
    is_active: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
    is_verified: statusFilter === 'verified' ? true : statusFilter === 'unverified' ? false : undefined,
    page,
    page_size: PAGE_SIZE,
  }, {
    // Keep showing current results while a new search/filter/page loads, so
    // isLoading stays false and the search input isn't unmounted (losing focus)
    placeholderData: keepPreviousData,
  });

  // Fetch aggregate stats separately so counters always reflect the full dataset
  const { data: userStats } = useUserStats();

  // Mutations
  const updateUserMutation = useUpdateUser();
  const createUserMutation = useCreateUser();
  const resendVerificationMutation = useResendVerification();

  const users = useMemo(() => usersResponse?.results || [], [usersResponse]);
  const totalCount = usersResponse?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const handleToggleUserStatus = async (user) => {
    try {
      await updateUserMutation.mutateAsync({
        id: user.id,
        is_active: !user.is_active
      });
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleResendVerification = async (user) => {
    try {
      await resendVerificationMutation.mutateAsync(user.id);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  // An enabled account that hasn't completed its invitation link can't log in yet,
  // so "Active" would be misleading — show it as awaiting registration instead.
  const renderStatusBadge = (user) => {
    if (!user.is_active) {
      return <Badge variant="secondary">{t('users.inactive')}</Badge>;
    }
    if (!user.is_email_verified) {
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-600">
          {t('users.awaitingRegistration')}
        </Badge>
      );
    }
    return <Badge variant="default">{t('users.active')}</Badge>;
  };

  const handleEditUser = (user) => {
    setEditUserData({
      id: user.id,
      username: user.username,
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      user_type: user.user_type
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    try {
      const { id, ...data } = editUserData;
      await updateUserMutation.mutateAsync({ id, ...data });
      setIsEditDialogOpen(false);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsViewDialogOpen(true);
  };

  const handleCreateUser = async () => {
    try {
      await createUserMutation.mutateAsync(newUserData);
      setIsCreateDialogOpen(false);
      setNewUserData({
        username: '',
        email: '',
        first_name: '',
        last_name: '',
        user_type: 'TEACHER'
      });
      toast({
        title: t('users.userCreated', 'User Created'),
        description: t('users.invitationSent', 'Invitation email sent to user to set their password'),
        variant: 'success',
      });
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.never');
    return new Date(dateString).toLocaleString('ga-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUserTypeDetail = (userType) => {
    return userTypeInfo[userType] || {
      name: userType,
      color: 'bg-gray-100 text-gray-800 border-gray-200'
    };
  };

  const stats = {
    total: userStats?.total ?? '—',
    active: userStats?.active ?? '—',
    verified: userStats?.verified ?? '—',
    byType: userStats?.by_type ?? {},
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header with loading skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                </CardTitle>
                <div className="h-4 w-4 bg-muted animate-pulse rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-8 w-12 bg-muted animate-pulse rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
        
        <Card>
          <CardContent className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">{t('users.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="text-center py-4">
                <div className="text-muted-foreground">--</div>
              </CardContent>
            </Card>
          ))}
        </div>
        
        <Card>
          <CardContent className="text-center py-8">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-destructive mb-2">
              {t('users.failedToLoad')}
            </h3>
            <p className="text-muted-foreground mb-4">
              {error?.message || t('users.errorMessage')}
            </p>
            <Button onClick={() => refetch()} variant="outline">
              {t('common.tryAgain')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header & Statistics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('users.totalUsers')}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('users.active')}
            </CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('users.verified')}
            </CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.verified}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {t('users.admins')}
            </CardTitle>
            <Crown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.byType.ADMIN || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex gap-4 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('users.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-input bg-background rounded-md text-sm"
          >
            <option value="">{t('users.allTypes')}</option>
            {Object.entries(userTypeInfo).map(([key, info]) => (
              <option key={key} value={key}>
                {info.name}
              </option>
            ))}
          </select>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-input bg-background rounded-md text-sm"
          >
            <option value="">{t('users.allStatus')}</option>
            <option value="active">{t('users.active')}</option>
            <option value="inactive">{t('users.inactive')}</option>
            <option value="verified">{t('users.verified')}</option>
            <option value="unverified">{t('users.unverified')}</option>
          </select>
        </div>
        
        <Button
          variant="outline"
          onClick={() => checkExpiredParentsMutation.mutate()}
          disabled={checkExpiredParentsMutation.isPending}
        >
          {checkExpiredParentsMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Send className="mr-2 h-4 w-4" />
          )}
          {checkExpiredParentsMutation.isPending
            ? t('users.resendExpiredParentsChecking')
            : t('users.resendExpiredParents')}
        </Button>

        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('users.newUser')}
        </Button>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('users.userManagement')}</CardTitle>
          <CardDescription>
            {totalCount} {t('users.usersDisplayed')}
            {totalPages > 1 && ` — ${t('users.page')} ${page} ${t('users.of')} ${totalPages}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('users.user')}</TableHead>
                <TableHead>{t('users.type')}</TableHead>
                <TableHead>{t('users.status')}</TableHead>
                <TableHead>{t('users.lastLogin')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const typeInfo = getUserTypeDetail(user.user_type);

                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          @{user.username} • {user.email}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={typeInfo.color}>
                        {typeInfo.name}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {renderStatusBadge(user)}
                        {user.is_email_verified ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(user.last_login)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>{t('users.actions')}</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleViewUser(user)}>
                            <Eye className="mr-2 h-4 w-4" />
                            {t('users.viewDetails')}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditUser(user)}>
                            <Edit className="mr-2 h-4 w-4" />
                            {t('users.editUser')}
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Shield className="mr-2 h-4 w-4" />
                            {t('users.managePermissions')}
                          </DropdownMenuItem>
                          {!user.is_email_verified && (
                            <DropdownMenuItem onClick={() => handleResendVerification(user)}>
                              <Mail className="mr-2 h-4 w-4" />
                              {t('users.resendVerification')}
                            </DropdownMenuItem>
                          )}
                          {currentUser?.user_type === 'ADMIN' && user.user_type !== 'ADMIN' && user.is_active && (
                            <DropdownMenuItem onClick={() => handleImpersonate(user)}>
                              <LogIn className="mr-2 h-4 w-4" />
                              {t('impersonation.start')}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleToggleUserStatus(user)}>
                            {user.is_active ? (
                              <>
                                <UserX className="mr-2 h-4 w-4" />
                                {t('users.deactivate')}
                              </>
                            ) : (
                              <>
                                <UserCheck className="mr-2 h-4 w-4" />
                                {t('users.activate')}
                              </>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
              <p className="text-sm text-muted-foreground">
                {t('users.showing')} {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, totalCount)} {t('users.of')} {totalCount}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('common.previous')}
                </Button>
                <span className="text-sm px-2">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages}
                >
                  {t('common.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('users.userDetails')}</DialogTitle>
            <DialogDescription>
              {t('users.completeUserInfo')}
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t('users.personalInfo')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('users.name')}</label>
                      <p>{selectedUser.first_name} {selectedUser.last_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('users.username')}</label>
                      <p className="font-mono">@{selectedUser.username}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('users.email')}</label>
                      <div className="flex items-center gap-2">
                        <p>{selectedUser.email}</p>
                        {selectedUser.is_email_verified ? (
                          <Badge variant="outline" className="text-green-600">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            {t('users.verified')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-600">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {t('users.unverified')}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">{t('users.systemInfo')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('users.userType')}</label>
                      <div>
                        <Badge className={getUserTypeDetail(selectedUser.user_type).color}>
                          {getUserTypeDetail(selectedUser.user_type).name}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('users.status')}</label>
                      {renderStatusBadge(selectedUser)}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('users.created')}</label>
                      <p className="text-sm">{formatDate(selectedUser.created_at)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">{t('users.lastLogin')}</label>
                      <p className="text-sm">{formatDate(selectedUser.last_login)}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('users.createNewUser', 'Create New User')}</DialogTitle>
            <DialogDescription>
              {t('users.createUserDescription', 'An invitation email will be sent to the user to set their password')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('users.firstName', 'First Name')} <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder={t('users.firstNamePlaceholder', 'John')}
                  value={newUserData.first_name}
                  onChange={(e) => setNewUserData({ ...newUserData, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('users.lastName', 'Last Name')} <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder={t('users.lastNamePlaceholder', 'Doe')}
                  value={newUserData.last_name}
                  onChange={(e) => setNewUserData({ ...newUserData, last_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('users.username')} <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder={t('users.usernamePlaceholder', 'johndoe')}
                value={newUserData.username}
                onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('users.email')} <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                placeholder={t('users.emailPlaceholder', 'john.doe@example.com')}
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('users.userType')} <span className="text-red-500">*</span>
              </label>
              <select
                value={newUserData.user_type}
                onChange={(e) => setNewUserData({ ...newUserData, user_type: e.target.value })}
                className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                {Object.entries(userTypeInfo).map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={
                  !newUserData.username ||
                  !newUserData.email ||
                  !newUserData.first_name ||
                  !newUserData.last_name ||
                  createUserMutation.isPending
                }
              >
                {createUserMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('users.creating', 'Creating...')}
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('users.createUser', 'Create User')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('users.editUser', 'Edit User')}</DialogTitle>
            <DialogDescription>
              {t('users.editUserDescription', 'Update user account details')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('users.firstName', 'First Name')} <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder={t('users.firstNamePlaceholder', 'John')}
                  value={editUserData.first_name}
                  onChange={(e) => setEditUserData({ ...editUserData, first_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t('users.lastName', 'Last Name')} <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder={t('users.lastNamePlaceholder', 'Doe')}
                  value={editUserData.last_name}
                  onChange={(e) => setEditUserData({ ...editUserData, last_name: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('users.username')} <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder={t('users.usernamePlaceholder', 'johndoe')}
                value={editUserData.username}
                onChange={(e) => setEditUserData({ ...editUserData, username: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('users.email')} <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                placeholder={t('users.emailPlaceholder', 'john.doe@example.com')}
                value={editUserData.email}
                onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t('users.userType')} <span className="text-red-500">*</span>
              </label>
              <select
                value={editUserData.user_type}
                onChange={(e) => setEditUserData({ ...editUserData, user_type: e.target.value })}
                className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
              >
                {Object.entries(userTypeInfo).map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                onClick={handleUpdateUser}
                disabled={
                  !editUserData.username ||
                  !editUserData.email ||
                  !editUserData.first_name ||
                  !editUserData.last_name ||
                  updateUserMutation.isPending
                }
              >
                {updateUserMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('users.saving', 'Saving...')}
                  </>
                ) : (
                  <>
                    <Edit className="mr-2 h-4 w-4" />
                    {t('users.saveChanges', 'Save Changes')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Resend Expired Parent Verifications Confirmation Dialog */}
      <Dialog open={isResendExpiredDialogOpen} onOpenChange={setIsResendExpiredDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('users.resendExpiredParentsConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('users.resendExpiredParentsConfirmDescription', { count: expiredParentCount })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsResendExpiredDialogOpen(false)}
              disabled={resendExpiredParentsMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => resendExpiredParentsMutation.mutate()}
              disabled={resendExpiredParentsMutation.isPending}
            >
              {resendExpiredParentsMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('users.resendExpiredParentsSending')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t('users.resendExpiredParents')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;